import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/BackButton';
import SEO from '@/components/SEO';
import { PageBreadcrumbs } from '@/components/StructuredData';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, Loader2, Car, Lock, Mic, Square, Flag, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import T from '@/components/StyledTranslation';

import { API } from '@/config';

// Typewriter effect component for AI messages
const TypewriterText = ({ text, speed = 5, onComplete, isRTL = false, scrollFn }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    if (!text) return;
    
    // للعربية والفارسية: عرض النص كاملاً مباشرة (بدون تأثير الكتابة)
    if (isRTL) {
      setDisplayedText(text);
      setIsComplete(true);
      scrollFn && scrollFn();
      onComplete?.();
      return;
    }
    
    // للغات الأخرى: كتابة حرف حرف
    setDisplayedText('');
    setIsComplete(false);
    let index = 0;
    
    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(prev => prev + text.slice(index, index + 5));
        scrollFn && scrollFn();
        index += 5;
      } else {
        clearInterval(timer);
        setIsComplete(true);
        onComplete?.();
      }
    }, speed);
    
    return () => clearInterval(timer);
  }, [text]);
  
  return <span>{displayedText}{!isComplete && <span className="animate-pulse">▋</span>}</span>;
};

// Translations for all 9 languages
const TEXTS = {
  recording: {
    ar: 'جارٍ التسجيل... اضغط لإيقاف',
    sv: 'Spelar in... tryck för att stoppa',
    en: 'Recording... tap to stop',
    tr: 'Kaydediliyor... durdurmak için dokunun',
    fa: 'در حال ضبط... برای توقف لمس کنید',
    so: 'Duubaya... taabo si aad u joojiso',
    pl: 'Nagrywanie... dotknij, aby zatrzymać',
    es: 'Grabando... toca para detener',
    de: 'Aufnahme... tippen zum Stoppen'
  },
  transcribing: {
    ar: 'جارٍ تحويل الصوت...',
    sv: 'Transkriberar...',
    en: 'Transcribing...',
    tr: 'Yazıya dönüştürülüyor...',
    fa: 'در حال تبدیل...',
    so: 'Qoraya...',
    pl: 'Transkrybuję...',
    es: 'Transcribiendo...',
    de: 'Transkribiere...'
  },
  micError: {
    ar: 'لا يمكن الوصول للميكروفون',
    sv: 'Kan inte komma åt mikrofonen',
    en: 'Cannot access microphone',
    tr: 'Mikrofona erişilemiyor',
    fa: 'دسترسی به میکروفون ممکن نیست',
    so: 'Ma heli karo makarafoonka',
    pl: 'Brak dostępu do mikrofonu',
    es: 'No se puede acceder al micrófono',
    de: 'Kein Zugriff auf Mikrofon'
  },
  voiceError: {
    ar: 'خطأ في تحويل الصوت',
    sv: 'Fel vid ljudtranskription',
    en: 'Voice transcription error',
    tr: 'Ses dönüştürme hatası',
    fa: 'خطا در تبدیل صدا',
    so: 'Khalad qorista codka',
    pl: 'Błąd transkrypcji głosu',
    es: 'Error de transcripción de voz',
    de: 'Sprachtranskriptionsfehler'
  },
  tapToStop: {
    ar: 'اضغط لإيقاف التسجيل',
    sv: 'Tryck för att stoppa',
    en: 'Tap to stop',
    tr: 'Durdurmak için dokunun',
    fa: 'برای توقف لمس کنید',
    so: 'Taabo si aad u joojiso',
    pl: 'Dotknij, aby zatrzymać',
    es: 'Toca para detener',
    de: 'Tippen zum Stoppen'
  },
  tapToRecord: {
    ar: 'اضغط للتسجيل الصوتي',
    sv: 'Tryck för att spela in',
    en: 'Tap to record',
    tr: 'Kaydetmek için dokunun',
    fa: 'برای ضبط لمس کنید',
    so: 'Taabo si aad u duubto',
    pl: 'Dotknij, aby nagrać',
    es: 'Toca para grabar',
    de: 'Tippen zum Aufnehmen'
  },
  placeholder: {
    ar: 'اسأل بأي لغة... سأفهمك تلقائياً',
    sv: 'Fråga på valfritt språk... jag förstår dig automatiskt',
    en: 'Ask in any language... I\'ll understand you automatically',
    tr: 'Herhangi bir dilde sorun... sizi otomatik anlayacağım',
    fa: 'به هر زبانی بپرسید... خودکار متوجه می‌شوم',
    so: 'Ku weydii luqad kasta... waan ku fahmi doonaa',
    pl: 'Zapytaj w dowolnym języku... zrozumiem automatycznie',
    es: 'Pregunta en cualquier idioma... te entenderé automáticamente',
    de: 'Fragen Sie in jeder Sprache... ich verstehe Sie automatisch'
  },
  subscriptionRequired: {
    ar: 'يجب الاشتراك لاستخدام المساعد الذكي',
    sv: 'Prenumeration krävs för att använda AI-assistenten',
    en: 'Subscription required to use AI Assistant',
    tr: 'AI Asistanı kullanmak için abonelik gerekli',
    fa: 'برای استفاده از دستیار هوشمند اشتراک لازم است',
    so: 'Rukhsad ayaa loo baahan yahay si aad u isticmaasho AI',
    pl: 'Wymagana subskrypcja do korzystania z asystenta AI',
    es: 'Se requiere suscripción para usar el asistente AI',
    de: 'Abonnement erforderlich für AI-Assistent'
  },
  aiForSubscribers: {
    ar: '🤖 المساعد الذكي للمشتركين',
    sv: '🤖 AI-Assistent för prenumeranter',
    en: '🤖 AI Assistant for Subscribers',
    tr: '🤖 Aboneler için AI Asistanı',
    fa: '🤖 دستیار هوشمند برای مشترکین',
    so: '🤖 AI Caawiye loogu talagalay qof rukhsad leh',
    pl: '🤖 Asystent AI dla subskrybentów',
    es: '🤖 Asistente AI para suscriptores',
    de: '🤖 AI-Assistent für Abonnenten'
  },
  subscribeNowDesc: {
    ar: 'اشترك الآن للحصول على إجابات فورية لأسئلتك!',
    sv: 'Prenumerera nu för att få omedelbara svar på dina frågor!',
    en: 'Subscribe now to get instant answers to your questions!',
    tr: 'Sorularınıza anında cevap almak için şimdi abone olun!',
    fa: 'برای دریافت پاسخ فوری سوالاتتان اشتراک بگیرید!',
    so: 'Hadda is diiwaan geli si aad u hesho jawaabo degdeg ah!',
    pl: 'Subskrybuj teraz, aby otrzymać natychmiastowe odpowiedzi!',
    es: '¡Suscríbete ahora para obtener respuestas instantáneas!',
    de: 'Abonnieren Sie jetzt für sofortige Antworten!'
  },
  subscribeNow: {
    ar: 'اشترك الآن 🤖',
    sv: 'Prenumerera nu 🤖',
    en: 'Subscribe Now 🤖',
    tr: 'Şimdi Abone Ol 🤖',
    fa: 'همین الان اشتراک بگیرید 🤖',
    so: 'Hadda is diiwaan geli 🤖',
    pl: 'Subskrybuj Teraz 🤖',
    es: 'Suscríbete Ahora 🤖',
    de: 'Jetzt Abonnieren 🤖'
  },
  cancel: {
    ar: 'إلغاء',
    sv: 'Avbryt',
    en: 'Cancel',
    tr: 'İptal',
    fa: 'لغو',
    so: 'Kansal',
    pl: 'Anuluj',
    es: 'Cancelar',
    de: 'Abbrechen'
  },
  reportError: { ar: 'الإبلاغ عن خطأ', sv: 'Rapportera fel', en: 'Report Error', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar Error', de: 'Fehler melden' },
  reportTitle: { ar: 'الإبلاغ عن خطأ', sv: 'Rapportera ett fel', en: 'Report an Error', tr: 'Bir Hata Bildir', fa: 'گزارش یک خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar un error', de: 'Einen Fehler melden' },
  reportType: { ar: 'نوع البلاغ', sv: 'Typ av rapport', en: 'Report Type', tr: 'Rapor Türü', fa: 'نوع گزارش', so: 'Nooca warbixinta', pl: 'Typ raportu', es: 'Tipo de reporte', de: 'Berichttyp' },
  incorrectInfo: { ar: 'معلومات غير صحيحة', sv: 'Felaktig information', en: 'Incorrect Information', tr: 'Yanlış Bilgi', fa: 'اطلاعات نادرست', so: 'Macluumaad khaldan', pl: 'Nieprawidłowe informacje', es: 'Información incorrecta', de: 'Falsche Information' },
  responseIssue: { ar: 'مشكلة في الرد', sv: 'Problem med svaret', en: 'Response Issue', tr: 'Yanıt Sorunu', fa: 'مشکل در پاسخ', so: 'Dhibaato jawaab', pl: 'Problem z odpowiedzią', es: 'Problema de respuesta', de: 'Antwortproblem' },
  otherIssue: { ar: 'مشكلة أخرى', sv: 'Annat problem', en: 'Other Issue', tr: 'Diğer Sorun', fa: 'مشکل دیگر', so: 'Dhibaato kale', pl: 'Inny problem', es: 'Otro problema', de: 'Anderes Problem' },
  details: { ar: 'التفاصيل', sv: 'Detaljer', en: 'Details', tr: 'Detaylar', fa: 'جزئیات', so: 'Faahfaahin', pl: 'Szczegóły', es: 'Detalles', de: 'Details' },
  describeIssue: { ar: 'يرجى وصف المشكلة...', sv: 'Beskriv problemet...', en: 'Please describe the issue...', tr: 'Lütfen sorunu açıklayın...', fa: 'لطفاً مشکل را شرح دهید...', so: 'Fadlan sharax dhibaatada...', pl: 'Proszę opisać problem...', es: 'Por favor describa el problema...', de: 'Bitte beschreiben Sie das Problem...' },
  submit: { ar: 'إرسال', sv: 'Skicka', en: 'Submit', tr: 'Gönder', fa: 'ارسال', so: 'Dir', pl: 'Wyślij', es: 'Enviar', de: 'Absenden' },
  reportSuccess: { ar: 'تم إرسال البلاغ بنجاح!', sv: 'Rapporten har skickats!', en: 'Report submitted successfully!', tr: 'Rapor başarıyla gönderildi!', fa: 'گزارش با موفقیت ارسال شد!', so: 'Warbixinta si guul leh ayaa loo diray!', pl: 'Raport wysłany pomyślnie!', es: '¡Reporte enviado con éxito!', de: 'Bericht erfolgreich gesendet!' },
  reportFailed: { ar: 'فشل في إرسال البلاغ', sv: 'Det gick inte att skicka rapporten', en: 'Failed to submit report', tr: 'Rapor gönderilemedi', fa: 'ارسال گزارش ناموفق بود', so: 'Waa lagu guul daraystay dirista warbixinta', pl: 'Nie udało się wysłać raportu', es: 'Error al enviar el reporte', de: 'Bericht konnte nicht gesendet werden' },
  voiceModeOn: { ar: '🎤 وضع المحادثة الصوتية', sv: '🎤 Röstchattläge', en: '🎤 Voice Chat Mode', tr: '🎤 Sesli Sohbet Modu', fa: '🎤 حالت چت صوتی', so: '🎤 Qaabka wada hadalka codka', pl: '🎤 Tryb czatu głosowego', es: '🎤 Modo de chat de voz', de: '🎤 Sprachchat-Modus' },
  voiceModeOff: { ar: '💬 وضع الكتابة', sv: '💬 Textläge', en: '💬 Text Mode', tr: '💬 Metin Modu', fa: '💬 حالت متن', so: '💬 Qaabka qoraalka', pl: '💬 Tryb tekstowy', es: '💬 Modo de texto', de: '💬 Textmodus' },
  tapToSpeak: { ar: 'اضغط وتحدث (يتوقف تلقائياً)', sv: 'Tryck och tala (stannar automatiskt)', en: 'Tap and speak (auto-stops)', tr: 'Dokun ve konuş (otomatik durur)', fa: 'ضربه بزنید و صحبت کنید', so: 'Taabo oo hadal', pl: 'Dotknij i mów (auto-stop)', es: 'Toca y habla (para automático)', de: 'Tippen und sprechen (stoppt automatisch)' },
  listening: { ar: 'جاري الاستماع... (سيتوقف تلقائياً)', sv: 'Lyssnar... (stannar automatiskt)', en: 'Listening... (auto-stops)', tr: 'Dinleniyor... (otomatik durur)', fa: 'در حال گوش دادن...', so: 'Dhageysanaya...', pl: 'Słucham... (auto-stop)', es: 'Escuchando... (para automático)', de: 'Höre zu... (stoppt automatisch)' },
  processing: { ar: 'جاري المعالجة...', sv: 'Bearbetar...', en: 'Processing...', tr: 'İşleniyor...', fa: 'در حال پردازش...', so: 'Waa la farsameeynayaa...', pl: 'Przetwarzanie...', es: 'Procesando...', de: 'Verarbeitung...' },
  stopAudio: { ar: 'إيقاف الصوت', sv: 'Stoppa ljud', en: 'Stop Audio', tr: 'Sesi Durdur', fa: 'توقف صدا', so: 'Jooji codka', pl: 'Zatrzymaj dźwięk', es: 'Detener audio', de: 'Audio stoppen' }
};

const getText = (key, lang) => TEXTS[key]?.[lang] || TEXTS[key]?.en || key;

const AssistantPage = () => {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]); // Track messages for voice chat
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  
  // Voice recording states (for text mode voice input only)
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const isRecordingRef = useRef(false);
  
  const FREE_AI_LIMIT = 10;
  
  // Generate or get guest session ID
  const getOrCreateGuestId = () => {
    let guestId = localStorage.getItem('guest_session_id');
    console.log('📱 Retrieved guest_session_id from localStorage:', guestId);
    if (!guestId) {
      guestId = 'guest_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('guest_session_id', guestId);
      console.log('📱 Created new guest_session_id:', guestId);
    }
    return guestId;
  };
  
  // Initialize guest ID on component mount (for guests only)
  const guestSessionId = useRef(!user ? getOrCreateGuestId() : null);
  
  // Get guest messages used from localStorage
  const getGuestMessagesUsed = () => {
    const stored = localStorage.getItem('guest_ai_messages_used');
    console.log('📱 Retrieved guest_ai_messages_used from localStorage:', stored);
    return stored ? parseInt(stored, 10) : 0;
  };
  
  const [aiMessagesUsed, setAiMessagesUsed] = useState(() => {
    // If logged in, use user's count, otherwise use localStorage for guests
    if (user) {
      return user.ai_messages_used || 0;
    }
    return getGuestMessagesUsed();
  });
  
  // التحقق إذا كان المستخدم مشترك أو لديه رسائل مجانية متبقية
  const isSubscribed = user?.subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';
  const isTrial = user?.subscription_status === 'trial';
  const isGuest = !user; // زائر بدون تسجيل
  
  // الزائر أو المستخدم المجاني يمكنه استخدام 10 رسائل
  const hasFreeMsgsLeft = (isGuest || isTrial) && aiMessagesUsed < FREE_AI_LIMIT;
  const canUseAI = isSubscribed || hasFreeMsgsLeft;

  useEffect(() => {
    document.body.classList.add('dm-assistant-page');
    return () => document.body.classList.remove('dm-assistant-page');
  }, []);

  // مزامنة عدد الرسائل من الـ Backend للزوار
  useEffect(() => {
    const syncGuestUsage = async () => {
      if (isGuest && guestSessionId.current) {
        console.log('📱 Syncing guest usage for:', guestSessionId.current);
        try {
          const response = await axios.get(`${API}/ai/guest-usage/${guestSessionId.current}`);
          console.log('📱 Backend response:', response.data);
          if (response.data && response.data.messages_used !== undefined) {
            const backendCount = response.data.messages_used;
            console.log('📱 Setting aiMessagesUsed to:', backendCount);
            setAiMessagesUsed(backendCount);
            localStorage.setItem('guest_ai_messages_used', backendCount.toString());
          }
        } catch (error) {
          // إذا فشل الطلب، نستخدم القيمة المحلية
          console.log('📱 Could not sync guest usage, using local count:', getGuestMessagesUsed());
        }
      }
    };
    syncGuestUsage();
  }, [isGuest]);

  useEffect(() => {
    scrollToBottom();
    // Keep messagesRef in sync with messages state
    messagesRef.current = messages;
  }, [messages, messages.length]);

  // دالة التمرير للأسفل - محسنة
  const scrollToBottom = () => {
    // تمرير فوري
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    // تمرير إضافي بعد delays مختلفة للتأكد من اكتمال الـ render
    [50, 150, 300, 500].forEach(delay => {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, delay);
    });
  };

  // مراقبة التمرير لإظهار/إخفاء زر السهم
  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom && messages.length > 0);
    }
  };

  // Text mode voice recording - iPhone/Safari compatible
  const startRecording = async () => {
    try {
      // Simple audio constraints for better iOS compatibility
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true
      });
      streamRef.current = stream;
      
      // Get best supported MIME type for current browser
      const getSupportedMimeType = () => {
        const types = [
          'audio/webm;codecs=opus',  // iPhone Safari
          'audio/webm',
          'audio/mp4',
          'audio/ogg'
        ];
        return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
      };
      
      const mimeType = getSupportedMimeType();
      console.log('📱 Using MIME type:', mimeType || 'default');
      
      const recorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      isRecordingRef.current = true;
      
      console.log('🎙️ Recording started');
      
      // Collect all audio chunks
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const detectedType = mimeType || 'audio/webm';
          const audioBlob = new Blob(audioChunksRef.current, { type: detectedType });
          
          // Check minimum size
          if (audioBlob.size < 1000) {
            console.log('Audio too short, size:', audioBlob.size);
            toast.warning(i18n.language === 'ar' ? 'التسجيل قصير جداً' : 'Recording too short');
            return;
          }
          
          setIsTranscribing(true);
          
          try {
            // Determine file extension
            const ext = detectedType.includes('mp4') ? 'mp4' : 
                        detectedType.includes('ogg') ? 'ogg' : 'webm';
            
            const formData = new FormData();
            formData.append('file', audioBlob, `recording.${ext}`);
            formData.append('language', i18n.language);
            
            const response = await axios.post(`${API}/voice/transcribe`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data.text) {
              setInput(response.data.text);
            } else {
              toast.warning(i18n.language === 'ar' ? 'لم أفهم، حاول مرة أخرى' : 'Could not understand, try again');
            }
          } catch (error) {
            console.error('Voice error:', error);
            toast.error(getText('voiceError', i18n.language));
          } finally {
            setIsTranscribing(false);
          }
        }
      };
      
      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        toast.error(getText('voiceError', i18n.language));
      };
      
      // Start recording with timeslice for iOS compatibility
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      mediaRecorder.start(isIOS ? 1000 : 100);
      setIsRecording(true);
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error(getText('micError', i18n.language));
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      isRecordingRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Cleanup stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      console.log('🛑 Recording stopped');
    }
  };
  
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const sendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // إنشاء سجل المحادثة (آخر 10 رسائل للسياق)
    const conversationHistory = messages.slice(-10).map(msg => ({
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));
    
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setLoading(true);

    try {
      // إرسال الرسالة - مع أو بدون token
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        language: i18n.language,
        guest_id: !token ? guestSessionId.current : undefined,
        conversation_history: conversationHistory
      }, { headers });
      
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: response.data.response, 
        isNew: true,
        relatedContent: response.data.related_content 
      }]);
      
      // تحديث العداد من الـ Backend (أكثر دقة)
      if (response.data.messages_used !== null && response.data.messages_used !== undefined) {
        const backendCount = response.data.messages_used;
        setAiMessagesUsed(backendCount);
        if (isGuest) {
          localStorage.setItem('guest_ai_messages_used', backendCount.toString());
        }
      } else if (isTrial || isGuest) {
        // Fallback إذا لم يُرجع الـ Backend العداد
        setAiMessagesUsed(prev => {
          const newCount = prev + 1;
          if (isGuest) {
            localStorage.setItem('guest_ai_messages_used', newCount.toString());
          }
          return newCount;
        });
      }
    } catch (error) {
      // التحقق من خطأ انتهاء الرسائل المجانية
      if (error.response?.status === 403) {
        // تحديث العداد ليعكس الحد الأقصى
        setAiMessagesUsed(FREE_AI_LIMIT);
        if (isGuest) {
          localStorage.setItem('guest_ai_messages_used', FREE_AI_LIMIT.toString());
        }
        setShowSubscribeModal(true);
        // لا نضيف رسالة خطأ - المودال سيظهر بدلاً من ذلك
        // حذف رسالة المستخدم الأخيرة لأنها لم تُرسل فعلياً
        setMessages(prev => prev.slice(0, -1));
      } else {
        toast.error(t('common.error'));
        setMessages(prev => [...prev, { 
          type: 'ai', 
          content: t('common.error'), 
          error: true 
        }]);
      }
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // التعامل مع إرسال الرسالة للمستخدم غير المشترك
  const handleSendMessage = () => {
    if (!canUseAI) {
      setShowSubscribeModal(true);
      return;
    }
    sendMessage();
  };
  
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('incorrect_info');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const isRTL = i18n.language === 'ar' || i18n.language === 'fa';

  // دالة لإرسال البلاغ
  const handleSubmitReport = async () => {
    if (!reportDetails.trim()) return;
    
    setSubmittingReport(true);
    try {
      await axios.post(
        `${API}/site-reports`,
        {
          url: `/ai-assistent`,
          report_type: reportType,
          details: reportDetails,
          item_type: 'ai_assistant'
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success(getText('reportSuccess', i18n.language));
      setShowReportModal(false);
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(getText('reportFailed', i18n.language));
    } finally {
      setSubmittingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex flex-col" data-testid="assistant-page">
      <SEO page="assistant" />
      <PageBreadcrumbs page="assistant" language={i18n.language} />
      <Navbar />
      
      <main className="flex-1 pt-16 pb-20">
        <div className="max-w-3xl w-full mx-auto p-4">
          {/* Back Button */}
          <BackButton to="/" className="mb-2" />
          
          {/* رسالة للزائر أو المستخدم المجاني - عدد الرسائل المتبقية */}
          {(isGuest || isTrial) && (
            <div className={`mb-4 flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold justify-center shadow-sm border ${
              hasFreeMsgsLeft 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-blue-400' 
                : 'bg-gradient-to-r from-red-500 to-orange-500 text-white border-red-400'
            }`}>
              {hasFreeMsgsLeft ? (
                <>
                  <MessageCircle className="w-5 h-5" />
                  <span>
                    {(() => {
                      const remaining = FREE_AI_LIMIT - aiMessagesUsed;
                      const translations = {
                        sv: `${remaining} gratis meddelanden kvar`,
                        en: `${remaining} free messages remaining`,
                        ar: `لديك ${remaining} رسائل مجانية متبقية`,
                        tr: `${remaining} ücretsiz mesaj kaldı`,
                        fa: `${remaining} پیام رایگان باقی مانده`,
                        so: `${remaining} fariin bilaash ah oo haray`,
                        pl: `Pozostało ${remaining} darmowych wiadomości`,
                        es: `${remaining} mensajes gratis restantes`,
                        de: `${remaining} kostenlose Nachrichten übrig`
                      };
                      return translations[i18n.language] || translations.en;
                    })()}
                  </span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>
                    {(() => {
                      const translations = {
                        sv: 'Dina gratis meddelanden är slut. Registrera dig och prenumerera!',
                        en: 'Your free messages are used up. Register and subscribe!',
                        ar: 'انتهت رسائلك المجانية. سجّل واشترك للمتابعة!',
                        tr: 'Ücretsiz mesajlarınız bitti. Kaydolun ve abone olun!',
                        fa: 'پیام‌های رایگان شما تمام شد. ثبت‌نام کنید و اشتراک بگیرید!',
                        so: 'Fariimaha bilaashka ah waa dhammaadeen. Is diiwaan geli oo subscribe!',
                        pl: 'Twoje darmowe wiadomości się skończyły. Zarejestruj się i subskrybuj!',
                        es: 'Tus mensajes gratis se han agotado. ¡Regístrate y suscríbete!',
                        de: 'Ihre kostenlosen Nachrichten sind aufgebraucht. Registrieren und abonnieren!'
                      };
                      return translations[i18n.language] || translations.en;
                    })()}
                  </span>
                </>
              )}
            </div>
          )}
          
          {/* Header */}
          <div className="text-center py-4 relative">
            {/* زر الإبلاغ - في الأعلى يمين */}
            <button
              onClick={() => setShowReportModal(true)}
              className="absolute top-2 start-0 p-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all z-10"
              title={getText('reportError', i18n.language)}
              data-testid="report-error-btn"
            >
              <Flag className="w-4 h-4" />
            </button>
            
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900 dark:to-emerald-800 flex items-center justify-center mb-3 shadow-lg border-4 border-white dark:border-slate-700">
              <Car className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2"><T k="assistant.title" /></h1>
            <p className="text-base text-slate-500 dark:text-slate-400"><T k="features.ai.desc" /></p>
          </div>

          {/* Messages - Only show when there are messages */}
          {messages.length > 0 && (
            <div ref={scrollRef} onScroll={handleScroll} className="flex-1 px-4 -mx-4 overflow-y-auto relative" style={{ maxHeight: 'calc(100vh - 350px)' }}>
              <div className="space-y-4 pb-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${idx}`}
                  >
                  <div className={`max-w-[85%] px-5 py-4 ${
                    msg.type === 'user' 
                      ? 'chat-bubble-user' 
                      : `chat-bubble-ai ${msg.error ? 'bg-red-50 dark:bg-red-950/50 text-red-600 dark:text-red-400' : ''}`
                  }`}
                  dir={/[\u0600-\u06FF]/.test(msg.content) ? 'rtl' : 'ltr'}
                  >
                    <p 
                      className="whitespace-pre-wrap text-base sm:text-lg leading-relaxed"
                      style={{ 
                        direction: /[\u0600-\u06FF]/.test(msg.content) ? 'rtl' : 'ltr',
                        textAlign: /[\u0600-\u06FF]/.test(msg.content) ? 'right' : 'left',
                        unicodeBidi: 'plaintext'
                      }}
                    >
                      {msg.type === 'ai' && msg.isNew ? (
                        <TypewriterText 
                          text={msg.content} 
                          speed={5}
                          isRTL={['ar', 'fa'].includes(i18n.language)}
                          scrollFn={scrollToBottom}
                          onComplete={() => {
                            setMessages(prev => prev.map((m, i) => 
                              i === idx ? { ...m, isNew: false } : m
                            ));
                          }}
                        />
                      ) : (
                        msg.content
                      )}
                    </p>
                    
                    {/* عرض إشارات المرور المتعلقة */}
                    {msg.type === 'ai' && msg.relatedContent?.traffic_signs?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 font-medium">
                          {i18n.language === 'ar' ? '🚦 إشارات مرور متعلقة:' : 
                           i18n.language === 'sv' ? '🚦 Relaterade trafikmärken:' : 
                           '🚦 Related traffic signs:'}
                        </p>
                        <div className="grid grid-cols-1 gap-3">
                          {msg.relatedContent.traffic_signs.map((sign, signIdx) => (
                            <div 
                              key={signIdx}
                              className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 hover:border-emerald-200 dark:hover:border-emerald-700 transition-colors"
                            >
                              {sign.image_url && (
                                <img 
                                  src={sign.image_url} 
                                  alt={sign.name}
                                  className="w-16 h-16 object-contain rounded-lg bg-slate-50 dark:bg-slate-700 p-1"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{sign.name}</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{sign.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="chat-bubble-ai px-5 py-4 flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                    <span className="text-base text-slate-500"><T k="assistant.thinking" /></span>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Recording indicator */}
          {/* زر التمرير للأسفل */}
          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              className="fixed bottom-32 left-1/2 -translate-x-1/2 z-40 w-10 h-10 bg-white dark:bg-slate-800 border-2 border-emerald-200 dark:border-emerald-700 rounded-full shadow-lg flex items-center justify-center hover:bg-emerald-50 dark:hover:bg-emerald-900/50 transition-all animate-bounce"
              data-testid="scroll-to-bottom-btn"
            >
              <ArrowDown className="w-5 h-5 text-emerald-600" />
            </button>
          )}

          {(isRecording || isTranscribing) && (
            <div className={`mb-4 px-4 py-3 rounded-xl flex items-center justify-center gap-3 ${
              isRecording ? 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800' : 
              'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800'
            }`}>
              {isRecording ? (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    {getText('listening', i18n.language)}
                  </span>
                </>
              ) : (
                <>
                  <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {getText('processing', i18n.language)}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Input Area - Text Only */}
          <form onSubmit={sendMessage} className="fixed bottom-0 left-0 right-0 bg-slate-50 dark:bg-slate-900 pt-3 pb-3 px-4 border-t border-slate-200 dark:border-slate-700 z-50 shadow-lg">
            <div className="flex gap-2 items-center max-w-4xl mx-auto">
              {/* Microphone Button */}
              <Button
                type="button"
                onClick={toggleRecording}
                disabled={loading || isTranscribing || !canUseAI}
                className={`h-12 w-12 min-w-[48px] rounded-xl transition-all flex-shrink-0 touch-manipulation ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 active:bg-red-700 animate-pulse' 
                    : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 active:bg-slate-400 text-slate-700 dark:text-slate-300'
                }`}
                title={isRecording ? getText('tapToStop', i18n.language) : getText('tapToRecord', i18n.language)}
                data-testid="mic-btn"
              >
                {isRecording ? (
                  <Square className="w-5 h-5 text-white" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </Button>
              
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={getText('placeholder', i18n.language)}
                className="flex-1 h-12 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 px-4 text-base dark:text-slate-100"
                disabled={loading || !canUseAI || isRecording || isTranscribing}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={!input.trim() || loading || !canUseAI}
                className="h-12 w-12 min-w-[48px] rounded-xl bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white flex-shrink-0 touch-manipulation"
                data-testid="send-btn"
                onClick={(e) => {
                  if (!canUseAI) {
                    e.preventDefault();
                    setShowSubscribeModal(true);
                  }
                }}
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* مودال الاشتراك */}
      {showSubscribeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="max-w-md w-full mx-4 bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 text-center animate-in fade-in zoom-in duration-300 relative">
            <button 
              onClick={() => setShowSubscribeModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              ✕
            </button>
            
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
              {getText('aiForSubscribers', i18n.language)}
            </h2>
            
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {getText('subscribeNowDesc', i18n.language)}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/priser')}
                className="w-full py-4 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg"
              >
                {getText('subscribeNow', i18n.language)}
              </button>
              
              <button
                onClick={() => setShowSubscribeModal(false)}
                className="w-full py-3 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                {getText('cancel', i18n.language)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال الإبلاغ عن خطأ */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-6 relative" dir={isRTL ? 'rtl' : 'ltr'}>
            <button 
              onClick={() => setShowReportModal(false)}
              className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors`}
            >
              ✕
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Flag className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{getText('reportTitle', i18n.language)}</h3>
                <p className="text-sm text-slate-500">AI-Assistent</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {getText('reportType', i18n.language)}
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="incorrect_info">{getText('incorrectInfo', i18n.language)}</option>
                  <option value="response_issue">{getText('responseIssue', i18n.language)}</option>
                  <option value="other">{getText('otherIssue', i18n.language)}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {getText('details', i18n.language)}
                </label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder={getText('describeIssue', i18n.language)}
                  rows={4}
                  className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50"
                >
                  {getText('cancel', i18n.language)}
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={!reportDetails.trim() || submittingReport}
                  className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 text-white font-semibold rounded-xl transition-colors"
                >
                  {submittingReport ? '...' : getText('submit', i18n.language)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantPage;
